import {
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  OnDestroy,
  Input,
  Output,
  EventEmitter
} from "@angular/core";
import { MatStepper } from "@angular/material/stepper";
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
  FormControl
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { NotificationService } from "app/main/shared/services/notification.service";
import {
  PathLocationStrategy,
  LocationStrategy,
  Location
} from "@angular/common";
import { ReverseOrderService } from "../reverse-order.service";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { ConfirmReverseComponent } from "../child-component/confirm-reverse/confirm-reverse.component";
import { SelectCourierPartnerComponent } from "app/main/shared/modules/select-courier-partner/select-courier-partner/select-courier-partner.component";
import { EditItemsComponent } from "../child-component/edit-items/edit-items.component";
import { RegexService } from "app/main/shared/services/regex.service";
import { RupeePaisePipe } from "app/main/shared/pipes/paise-to-rupee.pipe";
import { FullScreenLoaderService } from "app/main/shared/modules/full-screen-loader/full-screen-loader.service";
import { WeightConversionPipe } from "app/main/shared/pipes/weight-conversion.pipe";
import { ReverseBoxDetailsComponent } from "../child-component/reverse-box-details/reverse-box-details.component";

@Component({
  selector: "app-reverse-order",
  templateUrl: "./reverse-order.component.html",
  styleUrls: ["./reverse-order.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy }
  ]
})
export class ReverseOrderComponent implements OnInit, OnDestroy {
  private _unsubscribeAll: Subject<any> = new Subject();
  @Input() order;
  @Input() multiPieceControl: FormControl;
  // @Input() singleBoxForm: FormGroup;
  @Input() multipleBoxShipmentTypeControl: FormControl;
  @Output() editOrderEvent = new EventEmitter();
  @Input() boxType: FormGroup;
  @Output() addBoxEvent = new EventEmitter();

  @Input() boxList: any[] = [];

  weightConversionPipe = new WeightConversionPipe();

  constructor(
    private _fb: FormBuilder,
    private _route: ActivatedRoute,
    public _notification: NotificationService,
    private _location: Location,
    private _reverseOrderService: ReverseOrderService,
    private _regex: RegexService,
    private _fullScreenLoader: FullScreenLoaderService
  ) {}

  itemsDetailsStep: FormArray;
  singleBoxStep: FormGroup;
  courierPartnerStep: FormGroup;
  singleBoxForm: FormGroup;
  reverseQCStep: FormGroup;
  rateTableData: any[] = [];
  orderData: any = {};
  orderId = this._route.snapshot?.params?.orderId;
  loadingIndicator = true;
  viewPreference: string = "tableView";
  viewsList: string[] = ["tableView", "cardView"];
  reverseQCEnabled: boolean = false;

  // pipe
  rupeePaisePipe = new RupeePaisePipe();

  // declarations
  @ViewChild("stepper") stepper: MatStepper;
  @ViewChild(ConfirmReverseComponent)
  confirmReverseComponent: ConfirmReverseComponent;

  // for 0th Step
  @ViewChild(EditItemsComponent)
  editItemsComponent: EditItemsComponent;

  @ViewChild(ReverseBoxDetailsComponent)
  reverseBoxDetailsComponent: ReverseBoxDetailsComponent;

  @ViewChild(SelectCourierPartnerComponent)
  selectCourierPartnerComponent: SelectCourierPartnerComponent;

  ngOnInit(): void {
    this.createForm();
    this.createBox();
    this.fetchOrderDetails();
  }

  goBack(): void {
    this._location.back();
  }

  fetchOrderDetails(): void {
    let apiCalled = false;

    this._reverseOrderService.currentOrder
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((res: any) => {
        if (!!Object.keys(res).length || apiCalled) {
          // console.log("res", res);
          this.orderData = res.orders || res;

          //re-creating form before patch items in formArray
          this.createItemsListForm(this.orderData);
          this.patchItemDetails(this.orderData);

          // hiding loader
          this.loadingIndicator = false;
        } else if (!apiCalled) {
          apiCalled = true;
          this._reverseOrderService.getOrder({ orderId: this.orderId });
        }
      });
  }

  viewPreferenceChange(currentView: string) {
    setTimeout(() => {
      this.viewPreference = currentView;
    }, 100);
  }

  createItemsListForm(orderData) {
    this.itemsDetailsStep = this._fb.array(
      [this.createItem(orderData)],
      [Validators.required]
    );
  }

  createForm() {
    this.courierPartnerStep = this._fb.group({
      courier: ["", [Validators.required]]
    });

    this.reverseQCStep = this._fb.group({
      qc_enable: [false],
      return_reason: ["Wrong size delivered"],
      rvp_type: ["open_box"],
      qc_template_id: [null],
      capture_min_images: [2],
      capture_max_images: [4],
      help_content: this._fb.group({
        description: ["Please verify product condition"],
        url: [null],
        is_mandatory: [true]
      })
    });
  }

  createItem(item): FormGroup {
    return this._fb.group({
      name: ["", [Validators.required]],
      sku: [""],
      price: [
        { value: "", disabled: true },
        [
          Validators.required,
          Validators.pattern(this._regex.nonZeroPositiveDecimalDigit)
        ]
      ],
      quantity: [
        "",
        [
          Validators.required,
          Validators.max(item?.quantity),
          Validators.pattern(this._regex.number)
        ]
      ],
      // Reverse QC specific fields
      product_name: [""],
      brand: [""],
      size: [""],
      color: [""],
      imei: [null],
      serial_no: [null],
      ean_barcode: [""],
      product_images: this._fb.array([]),
      questions: this._fb.array([])
    });
  }

  createBox() {
    this.singleBoxStep = this._fb.group({
      length: [
        "",
        [
          Validators.required,
          Validators.pattern(this._regex.nonZeroPositiveDecimalDigit)
        ]
      ],
      breadth: [
        "",
        [
          Validators.required,
          Validators.pattern(this._regex.nonZeroPositiveDecimalDigit)
        ]
      ],
      height: [
        "",
        [
          Validators.required,
          Validators.pattern(this._regex.nonZeroPositiveDecimalDigit)
        ]
      ],
      weight: [
        "",
        [
          Validators.required,
          Validators.pattern(this._regex.nonZeroPositiveDecimalDigit)
        ]
      ],
      box: [""]
    });
  }

  removeItem(index): void {
    const arr = this.itemsDetailsStep as FormArray;
    arr.removeAt(index);
  }

  patchItemDetails(order): void {
    const itemsFormArray = this.itemsDetailsStep as FormArray;
    itemsFormArray.clear();
    // patching product information > items
    order?.items?.forEach((item) => {
      item = this.transformItem(item);
      const itemGroup = this.createItem(item);
      itemGroup.patchValue(item);
      
      // Prefill product_name with item name
      itemGroup.patchValue({
        product_name: item.product_name || item.name
      });
      
      // Subscribe to name changes to sync with product_name
      const nameControl = itemGroup.get('name');
      const productNameControl = itemGroup.get('product_name');
      if (nameControl && productNameControl) {
        nameControl.valueChanges
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe(value => {
            productNameControl.setValue(value, { emitEvent: false });
          });
      }
      
      // Patch product_images if they exist
      if (item.product_images && item.product_images.length > 0) {
        const imagesArray = itemGroup.get("product_images") as FormArray;
        item.product_images.forEach((imageUrl: string) => {
          imagesArray.push(this._fb.control(imageUrl));
        });
      }
      
      itemsFormArray.push(itemGroup);
    });
  }

  transformItem(item) {
    return {
      ...item,
      price: this.rupeePaisePipe.transform(item.price, "rupee")
    };
  }

  updateOrder() {
    this._fullScreenLoader.show();
    let items = this.itemsDetailsStep.getRawValue();

    items = items.map((item) => {
      item.price = this.rupeePaisePipe.transform(item.price, "paise");
      return item;
    });

    let boxDimensions = this.singleBoxStep.getRawValue(),
      length = Number(boxDimensions?.length),
      height = Number(boxDimensions?.height),
      breadth = Number(boxDimensions?.breadth),
      weight = Number(boxDimensions?.weight);

    const body: any = {
      SGORDERID: this.orderId,
      orderTotal: this.orderData?.orderTotal,
      items,
      length,
      height,
      weight,
      breadth
    };

    // Add reverseQC payload if enabled
    if (this.reverseQCEnabled && this.reverseQCStep) {
      const reverseQCData = this.reverseQCStep.value;
      const itemsData = this.itemsDetailsStep.getRawValue();
      
      const reverseQCItems = itemsData.map((item: any) => ({
        product_name: item.product_name || item.name,
        brand: item.brand || "",
        size: item.size || "",
        color: item.color || "",
        imei: item.imei || null,
        serial_no: item.serial_no || null,
        ean_barcode: item.ean_barcode || "",
        product_images: item.product_images || [],
        questions: item.questions || [],
        quantity: item.quantity || 1
      }));

      body.reverseQC = {
        qc_enable: reverseQCData.qc_enable,
        return_reason: reverseQCData.return_reason || "Wrong size delivered",
        rvp_type: reverseQCData.rvp_type || "open_box",
        items: reverseQCItems,
        qc_template_id: reverseQCData.qc_template_id || null,
        capture_min_images: reverseQCData.capture_min_images || 2,
        capture_max_images: reverseQCData.capture_max_images || 4,
        help_content: {
          description:
            reverseQCData.help_content?.description ||
            "Please verify product condition",
          url: reverseQCData.help_content?.url || null,
          is_mandatory: reverseQCData.help_content?.is_mandatory ?? true
        }
      };
    }

    this._reverseOrderService.updateOrder(body).subscribe((res) => {
      this._fullScreenLoader.hide();
      if (res.success) {
        this._reverseOrderService.currentOrder.next(res.order);

        if (res.rates) {
          this.assignRates(res.rates);
        }
        this.moveNext();
      } else {
        this._reverseOrderService.currentOrder.next({});
        this.goBack();
        this._notification.show(res.msg);
      }
    });
  }

  assignRates(rates) {
    this.rateTableData = rates.price.sort(
      (a, b) => a.price.price - b.price.price
    );
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    // for resetting currentOrder to empty object when component is destroyed
    this._reverseOrderService.currentOrder.next({});
  }

  handleStepperNext() {
    const { selectedIndex } = this.stepper;

    switch (selectedIndex) {
      case 0:
        // for Review Items Step
        if (this.itemsDetailsStep.invalid) {
          this.editItemsComponent.markFormAsTouched();
        } else if (this.reverseQCEnabled && !this.validateQCImages()) {
          this._notification.show("Please upload at least 1 image for each item when Reverse QC is enabled");
        } else {
          this.moveNext();
        }
        break;

      case 1:
        // for Box Details Step
        if (this.singleBoxStep.invalid) {
          this.reverseBoxDetailsComponent.markFormAsTouched();
        } else this.updateOrder();
        break;

      case 2:
        // for Select Courier Partner Step
        if (this.courierPartnerStep.invalid) {
          this.selectCourierPartnerComponent.markFormAsTouched();
        } else this.moveNext();
        break;

      default:
        break;
    }
  }

  handleStepperPrevious() {
    const { selectedIndex } = this.stepper;

    switch (selectedIndex) {
      // for Select Courier Partner Step
      case 1:
        this.courierPartnerStep.reset();
        this.movePrevious();
        break;

      default:
        this.movePrevious();
        break;
    }
  }

  moveNext() {
    this.stepper.next();
  }

  movePrevious() {
    this.stepper.previous();
  }

  onReverseQCChange(enabled: boolean) {
    this.reverseQCEnabled = enabled;
    this.reverseQCStep.patchValue({ qc_enable: enabled });
  }

  validateQCImages(): boolean {
    // Check if each item has at least 1 image when Reverse QC is enabled
    for (let i = 0; i < this.itemsDetailsStep.length; i++) {
      const item = this.itemsDetailsStep.at(i) as FormGroup;
      const images = item.get("product_images") as FormArray;
      if (!images || images.length === 0) {
        return false;
      }
    }
    return true;
  }

  addImageUrl(itemIndex: number, imageUrl: string) {
    const item = this.itemsDetailsStep.at(itemIndex) as FormGroup;
    const imagesArray = item.get("product_images") as FormArray;
    imagesArray.push(this._fb.control(imageUrl));
    // Trigger change detection in child component
    if (this.editItemsComponent) {
      this.editItemsComponent.triggerChangeDetection();
    }
  }

  removeImageUrl(itemIndex: number, imageIndex: number) {
    const item = this.itemsDetailsStep.at(itemIndex) as FormGroup;
    const imagesArray = item.get("product_images") as FormArray;
    imagesArray.removeAt(imageIndex);
    // Trigger change detection in child component
    if (this.editItemsComponent) {
      this.editItemsComponent.triggerChangeDetection();
    }
  }

  uploadImage(file: File, itemIndex: number): void {
    this._fullScreenLoader.show();
    
    const formData = new FormData();
    formData.append("photo", file);
    formData.append("allowedTypes", "image/png,image/jpeg,image/jpg");

    this._reverseOrderService.uploadFile(formData).subscribe(
      (res: any) => {
        this._fullScreenLoader.hide();
        if (res.success && res.files && res.files.length > 0) {
          const imageUrl = res.files[0].url;
          this.addImageUrl(itemIndex, imageUrl);
          this._notification.show("Image uploaded successfully");
        } else {
          this._notification.show(res.msg || "Failed to upload image");
        }
      },
      (error) => {
        this._fullScreenLoader.hide();
        this._notification.show("Error uploading image");
      }
    );
  }
}
